import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import {
  Box,
  Typography,
  Tooltip,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Settings,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  AdminPanelSettings,
  Tune,
  BarChart as BarChartIcon,
  Science as ScienceIcon,
  Storefront,
  Shield,
} from '@mui/icons-material';

interface NavItem {
  label: string;
  icon: React.ReactElement;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', icon: <DashboardIcon />, path: '/dashboard' },
  { label: 'My Strategies', icon: <TrendingUp />, path: '/strategies' },
  { label: 'Analytics', icon: <BarChartIcon />, path: '/analytics' },
  { label: 'Research', icon: <ScienceIcon />, path: '/research' },
  { label: 'Control Panel', icon: <Tune />, path: '/settings/control-panel' },
  { label: 'Marketplace', icon: <Storefront />, path: '/marketplace' },
  { label: 'Settings', icon: <Settings />, path: '/settings' },
];

const SIDEBAR_EXPANDED = 240;
const SIDEBAR_COLLAPSED = 64;

const Sidebar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(true);
  const { mobileOpen, closeMobile } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const sidebarWidth = open ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    // For /settings, only match if not on /settings/control-panel
    if (path === '/settings') {
      return location.pathname.startsWith('/settings') && !location.pathname.startsWith('/settings/control-panel');
    }
    return location.pathname.startsWith(path);
  };

  const handleNav = (path: string) => {
    navigate(path);
    if (isMobile) closeMobile();
  };

  const sidebarContent = (
    <Box
      component="nav"
      aria-label="Primary navigation"
      sx={{
        width: isMobile ? SIDEBAR_EXPANDED : sidebarWidth,
        minWidth: isMobile ? SIDEBAR_EXPANDED : sidebarWidth,
        transition: 'width 0.2s ease, min-width 0.2s ease',
        backgroundColor: (theme.palette as any).sidebar?.main || '#2A6666',
        display: 'flex',
        flexDirection: 'column',
        borderRight: `1px solid ${(theme.palette as any).sidebar?.active || '#3A8C8C'}33`,
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          height: 48,
          display: 'flex',
          alignItems: 'center',
          px: open ? 2 : 0,
          justifyContent: open ? 'flex-start' : 'center',
          cursor: 'pointer',
        }}
        onClick={() => handleNav('/dashboard')}
      >
        {open ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box component="img" src="/logo.svg" alt="Quantato" sx={{ width: 28, height: 28, borderRadius: '6px' }} />
            <Box>
              <Typography variant="h6" noWrap sx={{ fontFamily: '"Fraunces", serif', fontWeight: 600, color: '#FFFFFF', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                Quantato
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.6rem', fontStyle: 'italic' }}>
                ideas into trades
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box component="img" src="/logo.svg" alt="Quantato" sx={{ width: 28, height: 28, borderRadius: '6px' }} />
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

      {/* Nav Items */}
      <List sx={{ flexGrow: 1, pt: 1, overflowY: 'auto' }}>
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.path);
          const btn = (
            <ListItemButton
              key={item.label}
              onClick={() => handleNav(item.path)}
              sx={{
                minHeight: 44,
                px: open ? 2 : 0,
                justifyContent: open ? 'flex-start' : 'center',
                borderLeft: active ? `3px solid ${(theme.palette as any).sidebar?.active || theme.palette.primary.main}` : '3px solid transparent',
                backgroundColor: active ? `${(theme.palette as any).sidebar?.active || theme.palette.primary.main}26` : 'transparent',
                '&:hover': {
                  backgroundColor: active
                    ? `${(theme.palette as any).sidebar?.active || theme.palette.primary.main}38`
                    : 'rgba(255,255,255,0.08)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 2 : 0,
                  justifyContent: 'center',
                  color: active ? theme.palette.primary.light : 'rgba(255,255,255,0.6)',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: active ? 600 : 400,
                    color: active ? '#fff' : 'rgba(255,255,255,0.7)',
                  }}
                />
              )}
            </ListItemButton>
          );

          return open ? (
            <React.Fragment key={item.label}>{btn}</React.Fragment>
          ) : (
            <Tooltip key={item.label} title={item.label} placement="right" arrow>
              {btn}
            </Tooltip>
          );
        })}
      </List>

      {/* Admin Section — only visible to admins */}
      {user?.is_admin && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
          {open && (
            <Typography
              variant="overline"
              sx={{
                px: 2,
                pt: 1.5,
                pb: 0.5,
                color: 'rgba(255,255,255,0.3)',
                fontSize: '0.65rem',
                letterSpacing: 1.5,
              }}
            >
              Admin
            </Typography>
          )}
          <List sx={{ pt: 0 }}>
            {[
              { label: 'Admin Panel', icon: <AdminPanelSettings />, path: '/admin' },
              { label: 'Usage Analytics', icon: <BarChartIcon />, path: '/admin/usage' },
              { label: 'Triage Config', icon: <Tune />, path: '/admin/triage' },
              { label: 'Compliance', icon: <Shield />, path: '/admin/compliance' },
            ].map((item) => {
              const active = location.pathname === item.path;
              const btn = (
                <ListItemButton
                  key={item.label}
                  onClick={() => handleNav(item.path)}
                  sx={{
                    minHeight: 40,
                    px: open ? 2 : 0,
                    justifyContent: open ? 'flex-start' : 'center',
                    borderLeft: active ? '3px solid #e74c3c' : '3px solid transparent',
                    backgroundColor: active ? 'rgba(231,76,60,0.10)' : 'transparent',
                    '&:hover': {
                      backgroundColor: active
                        ? 'rgba(231,76,60,0.16)'
                        : 'rgba(255,255,255,0.08)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 2 : 0,
                      justifyContent: 'center',
                      color: active ? '#e74c3c' : 'rgba(255,255,255,0.6)',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 13,
                        fontWeight: active ? 600 : 400,
                        color: active ? '#fff' : 'rgba(255,255,255,0.7)',
                      }}
                    />
                  )}
                </ListItemButton>
              );
              return open ? (
                <React.Fragment key={item.label}>{btn}</React.Fragment>
              ) : (
                <Tooltip key={item.label} title={item.label} placement="right" arrow>
                  {btn}
                </Tooltip>
              );
            })}
          </List>
        </>
      )}

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

      {/* Collapse Toggle — hidden on mobile */}
      {!isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <IconButton
            onClick={() => setOpen((prev) => !prev)}
            size="small"
            aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
            sx={{ color: 'rgba(255,255,255,0.5)' }}
          >
            {open ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  // Mobile: render as temporary drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={closeMobile}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { backgroundColor: (theme.palette as any).sidebar?.main || '#2A6666', borderRight: 'none' } }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Desktop: render as fixed sidebar
  return sidebarContent;
};

export { Sidebar };
export default Sidebar;
